import type {
  Attribute,
  FieldId,
  FieldValue,
  FieldValueType,
} from "../../../types";
import { getValueById } from "../../../utils/field-utils";
import {
  type Option,
  getStaticFieldOptions,
} from "../../../utils/option-utils";
import { extractValueFromValueLabel } from "../../../utils/value-label-utils";

/**
 * 静的選択肢を取得する純粋関数
 *
 * @param field - フィールド属性
 * @returns 標準化された選択肢配列
 */
export const selectStaticOptions = (field: Attribute): Option[] => {
  return getStaticFieldOptions(field);
};

/**
 * 依存元フィールドの値を取得する純粋関数
 * dependencyInfo.sourceFieldから値を抽出
 *
 * @param field - フィールド属性
 * @param allValues - 全フィールド値の配列
 * @param fieldId - 現在のフィールドID
 * @returns 依存元フィールドの値 (文字列またはnull)
 */
export const selectSourceValue = (
  field: Attribute,
  allValues: FieldValue[],
  fieldId: FieldId,
): string | null => {
  if (
    !field.dependencyInfo?.sourceField ||
    !allValues ||
    !Array.isArray(allValues)
  ) {
    return null;
  }

  // sourceFieldが文字列の場合、fieldIdから依存元フィールドのIDを構築
  if (typeof field.dependencyInfo.sourceField === "string") {
    const fieldIdParts = fieldId.split(".");

    if (fieldIdParts.length >= 5) {
      // collectionId.collectionIndex.groupId.groupIndex.attributeId の形式
      fieldIdParts[4] = field.dependencyInfo.sourceField; // attributeIdを依存元フィールドIDに置き換え
      const targetFieldId = fieldIdParts.join(".");

      const value = getValueById(allValues, targetFieldId as FieldId);

      return extractValueFromValueLabel(value) || null;
    }
  }

  return null;
};

/**
 * 動的選択肢を使用すべきかどうかを判定する純粋関数
 *
 * @param hasStaticOptions - 静的選択肢があるかどうか
 * @param hasSourceValueChanged - 依存元の値が変更されたかどうか
 * @param sourceValue - 依存元フィールドの現在の値
 * @returns 動的選択肢を使用すべきかどうか
 */
export const selectShouldUseDynamicOptions = (
  hasStaticOptions: boolean,
  hasSourceValueChanged: boolean,
  sourceValue: string | null,
): boolean => {
  // 静的な選択肢がない場合は常に動的選択肢を使用
  if (!hasStaticOptions) {
    return true;
  }

  // 依存元フィールドに値がある場合は動的選択肢を使用
  // （初期値がある場合も含む）
  if (sourceValue) {
    return true;
  }

  // 依存元フィールドの値が変更された場合
  return hasSourceValueChanged;
};

/**
 * 使用する選択肢を決定する純粋関数
 *
 * @param shouldUseDynamic - 動的選択肢を使用するかどうか
 * @param dynamicOptions - 動的選択肢の配列
 * @param staticOptions - 静的選択肢の配列
 * @returns 使用する選択肢配列
 */
export const selectActiveOptions = (
  shouldUseDynamic: boolean,
  dynamicOptions: Option[],
  staticOptions: Option[],
): Option[] => {
  return shouldUseDynamic ? dynamicOptions : staticOptions;
};

/**
 * 選択肢が空かどうかを判定する純粋関数
 *
 * @param shouldUseDynamic - 動的選択肢を使用するかどうか
 * @param dynamicIsEmpty - 動的選択肢が空かどうか
 * @param staticOptions - 静的選択肢の配列
 * @returns 選択肢が空かどうか
 */
export const selectIsEmpty = (
  shouldUseDynamic: boolean,
  dynamicIsEmpty: boolean,
  staticOptions: Option[],
): boolean => {
  return shouldUseDynamic ? dynamicIsEmpty : staticOptions.length === 0;
};

/**
 * フィールドIDから依存元フィールドIDを構築する純粋関数
 *
 * @param fieldId - 現在のフィールドID
 * @param sourceFieldCode - 依存元フィールドのコード
 * @returns 構築された依存元フィールドID (成功時) または null
 */
export const selectDependencyFieldId = (
  fieldId: FieldId,
  sourceFieldCode: string,
): FieldId | null => {
  const fieldIdParts = fieldId.split(".");
  if (fieldIdParts.length >= 5) {
    // collectionId.collectionIndex.groupId.groupIndex.attributeId の形式
    fieldIdParts[4] = sourceFieldCode; // attributeIdを依存元フィールドIDに置き換え
    return fieldIdParts.join(".") as FieldId;
  }
  return null;
};

/**
 * 入力値から表示用の値を抽出する純粋関数
 *
 * @param inputValue - 入力値（FieldValueType）
 * @returns 表示用文字列
 */
export const selectDisplayValue = (inputValue: FieldValueType): string => {
  return extractValueFromValueLabel(inputValue);
};

/**
 * APIコール条件を判定する純粋関数
 *
 * @param shouldUseDynamicOptions - 動的選択肢を使用するかどうか
 * @param sourceFieldCode - 依存元フィールドのコード
 * @param targetFieldCode - 依存先フィールドのコード
 * @param sourceValue - 依存元の値
 * @returns APIコールを実行すべきかどうか
 */
export const selectShouldCallApi = (
  shouldUseDynamicOptions: boolean,
  sourceFieldCode?: string,
  targetFieldCode?: string,
  sourceValue?: string | null,
): boolean => {
  return (
    shouldUseDynamicOptions &&
    !!(sourceFieldCode && targetFieldCode && sourceValue)
  );
};
